/**
 * AISidebar.tsx
 *
 * Three elements, one store: a collapsible rail in the layout flow at `lg` and above,
 * a fixed overlay panel below it, and — new — a trigger that can actually open that
 * overlay.
 *
 * ── Why the trigger exists ──────────────────────────────────────────────────────
 * The overlay panel has always been mounted and has always had a close button, but
 * every *open* control lived inside the desktop rail, which is `hidden lg:flex`. So
 * below 1024px the assistant could be closed and never opened. The only way in was a
 * page calling `openSidebar` directly.
 *
 * It sits at the right edge, vertically centred: the same edge the desktop rail
 * occupies, and clear of the bottom-right corner where the floating call widgets and
 * pop-up notifications live.
 *
 * ── The trigger is now two elements, split at `md` ──────────────────────────────
 * Being fixed and vertically centred put it over whatever scrolls past the right edge,
 * and as a real button at `z-30` (above `<main>`'s `z-10`) it won the hit test. Eight
 * tickets reported taps intended for a page's primary action opening the assistant
 * instead. Below `md` it therefore lives in the shell's mobile header, in the layout
 * flow, where it cannot overlap anything — see `AIAssistantTrigger.tsx`.
 *
 * The tab below keeps 768–1023 only. That band has no header (the shell's is
 * `md:hidden`) and widening the header's visibility would take 48px of content height
 * from every routed page in it, which is the original reason this floated at all.
 *
 * The whole component is mounted behind the AI Assistant permission in `Layout.tsx`,
 * so the trigger inherits that gate rather than re-checking it.
 */
import { Sparkles, PanelRightClose, PanelRightOpen } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useIsBelow } from '@/hooks/useBreakpoint';
import { useVisualViewportVars } from '@/hooks/useVisualViewportVars';
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock';
import { useAISidebarStore, buildContextKey, selectSession } from '@/store/aiSidebarStore';
import { useResolvedPageContext } from './useResolvedPageContext';
import { PatientContextPanel } from './PatientContextPanel';
import { AppointmentContextPanel } from './AppointmentContextPanel';
import { LeadContextPanel } from './LeadContextPanel';
import { PlanContextPanel } from './PlanContextPanel';
import { SidebarChat } from './SidebarChat';


export function AISidebar() {
  const isOpen = useAISidebarStore((s) => s.isOpen);
  const closeSidebar = useAISidebarStore((s) => s.closeSidebar);
  const toggle = useAISidebarStore((s) => s.toggle);

  // Resolved rather than read straight from the store: pages that never call
  // usePageContext would otherwise leave the previous page's context in place.
  const { pageContext, contextType, contextId } = useResolvedPageContext();

  // Read from the router rather than pageContext.route: that one is a resolved,
  // human-facing label source, and this needs the raw path.
  const onAssistantPage = useLocation().pathname.startsWith('/ai-assistant');

  const hasEntity = contextType && contextId;

  // The entity panels run an LLM call server-side, so they load only once the
  // user has started this session (pressed Summarize or asked a question).
  const contextKey = buildContextKey(pageContext?.route, contextType, contextId);
  const started = useAISidebarStore((s) => selectSession(s, contextKey).started);

  // Everything below this line concerns the overlay only. `isOpen` is shared with the
  // desktop rail, so both effects are gated on the viewport as well — an ungated one
  // would reach `lg` and above, where the overlay is not even displayed.
  const isOverlay = useIsBelow('lg');

  // Pin the overlay to the visible region while the on-screen keyboard is open. See
  // the hook's docblock: the keyboard shrinks the visual viewport but not the layout
  // viewport, and a `fixed` panel follows the layout viewport.
  useVisualViewportVars(isOpen && isOverlay);

  // Scroll lock. Below `md` the shell deliberately releases its viewport lock
  // (Layout.tsx) and the document becomes the scroller, so without this the page slides
  // around under the overlay while the keyboard is opening.
  //
  // Shared with Modal through one refcounted owner rather than written here directly:
  // a modal opened from inside this overlay used to unlock the page for the overlay
  // when it closed, because both components were assigning the same single property.
  useBodyScrollLock(isOpen && isOverlay);

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/20 z-30 lg:hidden"
          onClick={closeSidebar}
        />
      )}

      {/* Desktop: always-visible, collapsible sidebar in layout flow */}
      <aside
        className={cn(
          'relative z-0 hidden lg:flex flex-col border-l border-slate-200 bg-white shrink-0 transition-all duration-300 ease-in-out h-full overflow-hidden',
          isOpen ? 'w-96' : 'w-12',
        )}
      >
        {/* Header */}
        <div
          className={cn(
            'flex items-center border-b border-slate-200 bg-gradient-to-r from-indigo-50 to-purple-50 shrink-0',
            isOpen ? 'justify-between px-4 py-3' : 'justify-center py-3',
          )}
        >
          {isOpen ? (
            <>
              <div className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-indigo-600" />
                <h2 className="text-sm font-semibold text-slate-800">AI Assistant</h2>
              </div>
              <button
                onClick={toggle}
                className="rounded-md p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                title="Collapse sidebar"
              >
                <PanelRightClose className="h-4 w-4" />
              </button>
            </>
          ) : (
            <button
              onClick={toggle}
              className="rounded-md p-1.5 text-indigo-600 hover:bg-indigo-100 transition-colors"
              title="Expand AI Assistant"
            >
              <Sparkles className="h-5 w-5" />
            </button>
          )}
        </div>

        {/* Collapsed state — vertical label */}
        {!isOpen && (
          <button
            onClick={toggle}
            className="flex-1 flex flex-col items-center justify-center gap-2 py-4 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50/50 transition-colors cursor-pointer"
          >
            <PanelRightOpen className="h-4 w-4" />
            <span
              className="text-xs font-medium tracking-wider"
              style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
            >
              AI ASSISTANT
            </span>
          </button>
        )}

        {/* Expanded content — chat owns its own scroll so the input stays pinned to the bottom */}
        {isOpen && (
          <div className="flex-1 min-h-0 flex flex-col">
            <SidebarChat
              contextType={contextType}
              contextId={contextId}
              pageContext={pageContext}
              topContent={
                hasEntity ? (
                  <ContextPanel type={contextType} id={contextId} enabled={started} />
                ) : null
              }
            />
          </div>
        )}
      </aside>

      {/*
        Below lg, the only way in. Hidden while open — the panel carries its own close
        control and the backdrop dismisses it — so it never competes with the backdrop
        for the same tap. Sized past the 44px touch floor here rather than waiting for
        the shared button treatment, which will not reach a raw button element.

        Also hidden on the assistant's own page: opening a contextual assistant from
        inside the assistant is redundant, and being a fixed overlay it floats on top
        of that page's chat. The panel itself stays mounted, so a sidebar that was
        already open there can still be closed.
      */}
      {!isOpen && !onAssistantPage && (
        <button
          type="button"
          onClick={toggle}
          aria-label="Open AI Assistant"
          title="AI Assistant"
          className={cn(
            // Tablet band only. Below `md` this moved into the shell's mobile header
            // (AIAssistantTrigger) because a fixed, vertically-centred button at z-30
            // sits over whatever scrolls past the right edge and wins the hit test —
            // eight tickets reported taps meant for page buttons opening the assistant.
            // The header is `md:hidden`, so 768–1023 has no header to move into and
            // keeps the tab; widening the header to `lg` would cost every page in that
            // band 48px of content height, which is the trade the docblock rejected.
            'fixed right-0 top-1/2 z-30 -translate-y-1/2 hidden md:flex lg:hidden',
            'items-center justify-center px-3 py-4',
            'rounded-l-lg border border-r-0 border-slate-200 bg-white text-indigo-600 shadow-lg',
            'hover:bg-indigo-50 transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          )}
        >
          <Sparkles className="h-5 w-5" />
        </button>
      )}

      {/*
        Mobile: fixed overlay.

        ── Width ────────────────────────────────────────────────────────────────────
        Full-bleed below `sm`. This replaces a `w-96 max-w-[calc(100vw-2rem)]` cap that
        deliberately left a 32px strip of the page showing so the backdrop stayed
        tappable; on a phone that reads as the panel not fitting the screen, and it was
        reported as a defect. The trade is real and is why the close button below carries
        a touch floor: under 640px the backdrop is fully covered, so that button is the
        only way out. From `sm` up the old 384px panel and its tappable strip are intact,
        which is what you want on a landscape phone and on tablet.

        ── Height and position ──────────────────────────────────────────────────────
        Not `top-0 h-full`. The on-screen keyboard does not shrink the layout viewport,
        and a `fixed` element is laid out against the layout viewport — so `h-full` kept
        the panel at its pre-keyboard height while the visible region shrank and slid
        down inside it, leaving the user looking at the panel's empty middle with the
        header off the top and the input bar under the browser's bottom bar. The two
        utilities track the visual viewport instead, falling back to `100dvh` / `0px`
        when no listener is attached. `pb-safe` clears the home indicator; it sits on the
        panel rather than inside the chat so it comes out of the pinned height.

        Only `transform` is transitioned, so height and offset follow the keyboard
        immediately instead of lagging its animation. Do not widen that transition.

        All of this is on a `lg:hidden` element — the desktop rail above is separate and
        is deliberately untouched.
      */}
      <aside
        className={cn(
          'fixed top-visual-viewport right-0 h-visual-viewport pb-safe w-full sm:w-96 bg-white border-l border-slate-200 shadow-xl z-40',
          'transition-transform duration-300 ease-in-out',
          'flex flex-col lg:hidden',
          isOpen ? 'translate-x-0' : 'translate-x-full',
        )}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-gradient-to-r from-indigo-50 to-purple-50">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-indigo-600" />
            <h2 className="text-sm font-semibold text-slate-800">AI Assistant</h2>
          </div>
          {/*
            Below `sm` the panel is full-bleed, so the backdrop is covered and this is
            the only way to dismiss the assistant — it has to be reachable. The icon is
            16px, so the target was 24px. Grown to 44px by the touch-target utilities
            in index.css, which use the same idiom as sheet.tsx's close: the negative
            margin keeps the layout box at 20px, so the header stays ~48px tall instead
            of growing to ~68px.

            Utilities rather than the `max-md:` variants this used to carry. This panel
            is `lg:hidden`, so it is still the rendered instance at 800px, where a
            `max-md:` target reverted to 24px — on an iPad, in portrait, with a coarse
            pointer. The utilities are gated on that band instead.

            It also had no accessible name at all — no aria-label, no title, no sr-only
            text — unlike the rail's equivalent, which has `title="Collapse sidebar"`.
          */}
          <button
            onClick={closeSidebar}
            aria-label="Close AI Assistant"
            className="rounded-md p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors touch-target-44 touch-target-inset"
          >
            <PanelRightClose className="h-4 w-4" />
          </button>
        </div>

        {/* Chat owns its own scroll so the input stays pinned to the bottom */}
        <div className="flex-1 min-h-0 flex flex-col">
          <SidebarChat
            contextType={contextType}
            contextId={contextId}
            pageContext={pageContext}
            topContent={
              hasEntity ? (
                <ContextPanel type={contextType} id={contextId} enabled={started} />
              ) : null
            }
          />
        </div>
      </aside>
    </>
  );
}


function ContextPanel({ type, id, enabled }: { type: string; id: number; enabled: boolean }) {
  switch (type) {
    case 'patient':
      return <PatientContextPanel patientId={id} enabled={enabled} />;
    case 'appointment':
      return <AppointmentContextPanel appointmentId={id} enabled={enabled} />;
    case 'lead':
      return <LeadContextPanel leadId={id} enabled={enabled} />;
    case 'plan':
      return <PlanContextPanel planId={id} enabled={enabled} />;
    default:
      return null;
  }
}
